import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

type DropdownKey = 'categories' | 'brands' | 'comparisons' | 'guides' | 'agents' | null;

export default function Navbar() {
  const [activeDropdown, setActiveDropdown] = useState<DropdownKey>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { label: 'Home', href: '/' },
    { label: 'CSSBuy Sheet', href: '/cssbuy-sheet-guide' },
    { label: 'FAQ', href: '/#faq' },
  ];

  const brandLinks = [
    { label: 'Nike', href: '/brands/nike' },
    { label: 'Adidas', href: '/brands/adidas' },
    { label: 'Supreme', href: '/brands/supreme' },
  ];

  const comparisonLinks = [
    { label: 'CSSBuy vs Kakobuy', href: '/comparison/cssbuy-vs-kakobuy' },
    { label: 'CSSBuy vs Superbuy', href: '/comparison/cssbuy-vs-superbuy' },
    { label: 'Kakobuy vs Superbuy', href: '/comparison/kakobuy-vs-superbuy' },
  ];

  const guideLinks = [
    { label: 'Shipping Guide', href: '/guides/shipping' },
    { label: 'QC Guide', href: '/guides/qc' },
  ];

  const agentLinks = [
    { label: 'Kakobuy Spreadsheet', href: '/kakobuy-spreadsheet' },
    { label: 'Superbuy Spreadsheet', href: '/superbuy-spreadsheet' },
    { label: 'Oopbuy Spreadsheet', href: '/oopbuy-spreadsheet' },
  ];

  const dropdowns: { key: DropdownKey; label: string; links: { label: string; href: string }[] }[] = [
    { key: 'categories', label: 'Categories', links: categories.map(c => ({ label: c.name, href: c.url })) },
    { key: 'brands', label: 'Brands', links: brandLinks },
    { key: 'comparisons', label: 'Comparisons', links: comparisonLinks },
    { key: 'guides', label: 'Guides', links: guideLinks },
    { key: 'agents', label: 'Agents', links: agentLinks },
  ];

  const toggleDropdown = (key: DropdownKey) => {
    setActiveDropdown(activeDropdown === key ? null : key);
  };

  const textColor = (defaultColor: string) =>
    scrolled ? 'text-foreground-700' : defaultColor;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-stack-line text-background-50 text-lg"></i>
            </div>
            <span className={`font-heading text-lg md:text-xl font-semibold whitespace-nowrap ${
              scrolled ? 'text-foreground-900' : 'text-background-50'
            }`}>
              CSSSpreadsheet
            </span>
          </a>

          <div className="hidden md:flex items-center gap-6" ref={dropdownRef}>
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={`text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${textColor('text-background-50/90')}`}
              >
                {item.label}
              </a>
            ))}

            {dropdowns.map((dd) => (
              <div key={dd.key} className="relative">
                <button
                  onClick={() => toggleDropdown(dd.key)}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${textColor('text-background-50/90')}`}
                >
                  {dd.label}
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 text-base ${
                    activeDropdown === dd.key ? 'rotate-180' : ''
                  }`}></i>
                </button>

                {activeDropdown === dd.key && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto">
                    {dd.links.map((link) => {
                      const isExternal = link.href.startsWith('http');
                      return isExternal ? (
                        <a
                          key={link.label}
                          href={link.href}
                          target="_blank"
                          rel="noopener nofollow"
                          className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap"
                          onClick={() => setActiveDropdown(null)}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <a
                          key={link.label}
                          href={link.href}
                          className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap"
                          onClick={() => setActiveDropdown(null)}
                        >
                          {link.label}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all ${
                scrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50/20 text-background-50 hover:bg-background-50/30 backdrop-blur-sm'
              }`}
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                scrolled ? 'text-foreground-900' : 'text-background-50'
              }`}
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}

            {dropdowns.map((dd) => (
              <div key={dd.key}>
                <div className="border-t border-background-200/70 my-1"></div>
                <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">{dd.label}</p>
                {dd.links.map((link) => {
                  const isExternal = link.href.startsWith('http');
                  return isExternal ? (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-3 py-2 text-sm text-foreground-700 hover:bg-accent-100 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      className="px-3 py-2 text-sm text-foreground-700 hover:bg-accent-100 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  );
                })}
              </div>
            ))}

            <div className="pt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-4 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}